import { LoadingOutlined } from '@ant-design/icons'
import { useScheduleContext } from '@components/OrderDispatch/Contexts/ScheduleContext'
import { ActionPopover } from '@components/OrderDispatch/TripGrid/ActionBlock/ActionPopover'
import { TrailerSwapActionBlock } from '@components/OrderDispatch/TripGrid/ActionBlock/TrailerSwapActionBlock'
import { ItemTypes } from '@constants/orderDispatch'
import { Vertical } from '@gravitate-js/excalibrr'
import { theme } from 'antd'
import classNames from 'classnames'
import { useMemo, useState } from 'react'
import { useDrop } from 'react-dnd'
import { useLockedSchedules } from '@hooks/useLockedSchedules'

export function ActionCell({ actionBlock, slotIndex, schedule, trip, trailerNames }) {
  const { token } = theme.useToken()
  const [isVisible, setIsVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const { addActionBlock, removeActionBlock, insertOrderBeforeActionBlock, insertAssignedBeforeActionBlock } =
    useScheduleContext()
  const { lockedSchedules } = useLockedSchedules()
  const optimizing = useMemo(
    () => lockedSchedules?.map((s) => s.id).includes(schedule?.id),
    [lockedSchedules, schedule?.id]
  )
  const isBlocked = useMemo(() => {
    if (!schedule?.invalid_credentials) return false
    return schedule.invalid_credentials?.some(
      (cred) => cred.dispatch_impact === 'Block Dispatch' && cred.status === 'Expired' && cred.is_blocked
    )
  }, [schedule?.invalid_credentials])

  const trailerToLeft = useMemo(() => {
    const slotsUpToThis = schedule.action_blocks.slice(0, slotIndex)
    let swappedConfig
    const lastFoundConfig = slotsUpToThis.findLast((item) => {
      return item[0]?.type === 'trailer_swap'
    })
    if (lastFoundConfig) {
      swappedConfig = lastFoundConfig[0]?.details.new_trailer_config
    }

    return swappedConfig || schedule?.current_trailer_config || schedule?.default_trailer_config || 'N/A'
  }, [schedule])

  const hasOrderBefore = !!trip.order_id

  const addAction = async (value) => {
    setIsVisible(false)

    const payload = {
      driver_schedule_id: schedule.id,
      type: 'trailer_swap',
      details: { new_trailer_config: value },
      slot_index: slotIndex,
    }
    await addActionBlock(payload)
    setIsLoading(false)
  }

  const removeAction = async () => {
    setIsLoading(true)
    const payload = {
      driver_schedule_id: schedule?.id,
      type: 'trailer_swap',
      slot_index: slotIndex,
    }
    await removeActionBlock(payload)
    setIsLoading(false)
  }

  const insertBefore = async (payload) => {
    setIsLoading(true)
    await insertOrderBeforeActionBlock(payload)
    setIsLoading(false)
  }
  const insertBeforeFromGrid = async (payload) => {
    setIsLoading(true)
    await insertAssignedBeforeActionBlock(payload)
    setIsLoading(false)
  }

  const [{ isOver, dragItem, canDrop }, drop] = useDrop({
    accept: [ItemTypes.TRAILER_SWAP, ItemTypes.ORDERROW, ItemTypes.TRIPGRIDCELL],
    collect: (mon) => ({
      isOver: !optimizing && !isBlocked && !!mon.isOver(),
      canDrop: !optimizing && !isBlocked && !!mon.canDrop(),
      dragItem: !optimizing && !isBlocked && mon.getItem(),
    }),
    canDrop: (item) => {
      const alreadyHadTrailerSwap = item.type === ItemTypes.TRAILER_SWAP && !!actionBlock
      const beforeLinkedOrLocked = schedule.trips[slotIndex + 1]?.locked || schedule.trips[slotIndex + 1]?.is_linked
      const isInsertWithOrderBefore =
        !hasOrderBefore && (item.type === ItemTypes.ORDERROW || item.type === ItemTypes.TRIPGRIDCELL)
      if (alreadyHadTrailerSwap || beforeLinkedOrLocked || isInsertWithOrderBefore) {
        return false
      }
      return true
    },
    drop: (item, monitor) => {
      if (item.type === ItemTypes.TRAILER_SWAP) {
        setIsVisible(true)
        setIsLoading(true)
        return
      }

      if (item.type === ItemTypes.ORDERROW) {
        const payload = {
          driver_schedule_id: schedule.id,
          action: 'insert_before_action',
          slot_index: slotIndex,
          version: schedule.version,
          order_id: item.order.id,
        }
        return insertBefore(payload)
      }

      if (item.type === ItemTypes.TRIPGRIDCELL) {
        const payload = {
          order_id: item.order.order_id,
          src_driver_schedule_id: item.scheduleId,
          src_slot_index: item.tripIndex,
          tgt_driver_schedule_id: schedule.id,
          tgt_slot_index: slotIndex,
          src_version: item.version,
          tgt_version: schedule.version,
          action: 'insert_before_action',
        }
        return insertBeforeFromGrid(payload)
      }
    },
  })

  const showBlock = !!actionBlock && !isLoading

  return (
    <>
      <ActionPopover
        isVisible={isVisible}
        trailerToLeft={trailerToLeft}
        cancelChange={() => {
          setIsLoading(false)
          setIsVisible(false)
        }}
        addAction={addAction}
        scheduleId={schedule.id}
        trailerNames={trailerNames}
      />
      <div
        className={classNames(
          `h-100 ab-drop-container`,
          { 'overlay bg-success-dim': isOver && !actionBlock && canDrop && dragItem.type === ItemTypes.TRAILER_SWAP },
          {
            'insert-border':
              isOver &&
              !!actionBlock &&
              canDrop &&
              (dragItem.type === ItemTypes.ORDERROW || dragItem.type === ItemTypes.TRIPGRIDCELL) &&
              hasOrderBefore,
          },
          { 'trip-grid-row no-order-optimizing-container ': isBlocked || optimizing }
        )}
        ref={!optimizing && !isBlocked ? drop : null}
      >
        {isLoading && (
          <Vertical verticalCenter horizontalCenter flex='1'>
            <LoadingOutlined color={token.colorInfo} />
          </Vertical>
        )}
        {showBlock && (
          <TrailerSwapActionBlock
            actionBlock={actionBlock}
            removeAction={removeAction}
            trailerToLeft={trailerToLeft}
            isBlocked={optimizing || isBlocked}
          />
        )}
      </div>
    </>
  )
}
